import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { Logo } from "./Logo";

export function Footer() {
  const { t, prefix, lang } = useI18n();
  const isSv = lang === "sv";

  const cols = [
    {
      title: isSv ? "Casinon" : "Casinos",
      links: [
        { href: `${prefix}/casino`, label: isSv ? "Alla casinon" : "All casinos" },
        {
          href: `${prefix}/${isSv ? "casino-med-svensk-licens" : "casinos-with-swedish-license"}`,
          label: isSv ? "Casinon med svensk licens" : "Swedish-licensed casinos",
        },
        {
          href: `${prefix}/${isSv ? "casino-utan-svensk-licens" : "casinos-without-swedish-license"}`,
          label: isSv ? "Casino utan svensk licens" : "Without Swedish license",
        },
        {
          href: `${prefix}/${isSv ? "casino-utan-spelpaus" : "casinos-without-spelpaus"}`,
          label: isSv ? "Casino utan Spelpaus" : "Without Spelpaus",
        },
        {
          href: `${prefix}/${isSv ? "nya-casinon" : "new-casinos"}`,
          label: isSv ? "Nya casinon" : "New casinos",
        },
        {
          href: `${prefix}/${isSv ? "live-casino" : "live-casino"}`,
          label: isSv ? "Live casino" : "Live casino",
        },
      ],
    },
    {
      title: isSv ? "Betalningar" : "Payments",
      links: [
        {
          href: `${prefix}/${isSv ? "betalningsmetoder" : "payment-methods"}`,
          label: isSv ? "Alla betalningsmetoder" : "All payment methods",
        },
        {
          href: `${prefix}/${isSv ? "betalningsmetoder/swish-casino" : "payment-methods/swish-casinos"}`,
          label: isSv ? "Casino med Swish" : "Casinos with Swish",
        },
        {
          href: `${prefix}/${isSv ? "betalningsmetoder/trustly" : "payment-methods/trustly"}`,
          label: "Trustly",
        },
        {
          href: `${prefix}/${isSv ? "betalningsmetoder/zimpler" : "payment-methods/zimpler"}`,
          label: "Zimpler",
        },
        {
          href: `${prefix}/${isSv ? "betalningsmetoder/brite" : "payment-methods/brite"}`,
          label: "Brite",
        },
        {
          href: `${prefix}/${isSv ? "betalningsmetoder/bankid" : "payment-methods/bankid"}`,
          label: "BankID",
        },
        {
          href: `${prefix}/${isSv ? "betalningsmetoder/bitcoin" : "payment-methods/bitcoin"}`,
          label: isSv ? "Krypto / Bitcoin" : "Crypto / Bitcoin",
        },
      ],
    },
    {
      title: isSv ? "Bonus & spel" : "Bonus & games",
      links: [
        {
          href: `${prefix}/${isSv ? "casino-bonus" : "casino-bonus"}`,
          label: isSv ? "Casino bonus" : "Casino bonus",
        },
        { href: `${prefix}/free-spins`, label: "Free spins" },
        {
          href: `${prefix}/${isSv ? "spelautomater" : "slots"}`,
          label: isSv ? "Spelautomater" : "Slots",
        },
        {
          href: `${prefix}/${isSv ? "speltillverkare" : "software-providers"}`,
          label: isSv ? "Speltillverkare" : "Software providers",
        },
        {
          href: `${prefix}/${isSv ? "betting" : "betting"}`,
          label: isSv ? "Sportbetting" : "Sports betting",
        },
        {
          href: `${prefix}/${isSv ? "esport-betting" : "esports-betting"}`,
          label: isSv ? "Esport betting" : "Esports betting",
        },
      ],
    },
    {
      title: isSv ? "Guider & licenser" : "Guides & licenses",
      links: [
        {
          href: `${prefix}/${isSv ? "guider" : "guides"}`,
          label: isSv ? "Alla guider" : "All guides",
        },
        {
          href: `${prefix}/${isSv ? "guider/svensk-spellicens" : "guides/swedish-gambling-license"}`,
          label: isSv ? "Svensk spellicens" : "Swedish license",
        },
        {
          href: `${prefix}/${isSv ? "guider/omsattningskrav" : "guides/wagering-requirements"}`,
          label: isSv ? "Omsättningskrav" : "Wagering",
        },
        {
          href: `${prefix}/${isSv ? "guider/rtp" : "guides/rtp"}`,
          label: "RTP",
        },
        {
          href: `${prefix}/${isSv ? "licenser" : "licenses"}`,
          label: isSv ? "Spellicenser" : "Licenses",
        },
      ],
    },
    {
      title: isSv ? "Ansvar" : "Safety",
      links: [
        {
          href: `${prefix}/${isSv ? "ansvarsfullt-spelande" : "responsible-gambling"}`,
          label: isSv ? "Ansvarsfullt spelande" : "Responsible gambling",
        },
        { href: `${prefix}/spelpaus`, label: "Spelpaus.se" },
        { href: `${prefix}/stodlinjen`, label: "Stödlinjen" },
        {
          href: `${prefix}/${isSv ? "spelinspektionen" : "swedish-gambling-authority"}`,
          label: "Spelinspektionen",
        },
        {
          href: `${prefix}/${isSv ? "svartlistade-casinon" : "blacklisted-casinos"}`,
          label: isSv ? "Under granskning" : "Under review",
        },
      ],
    },
  ];

  return (
    <footer className="relative mt-20 overflow-hidden bg-ink-950 text-ink-200">
      <div className="absolute inset-0 hero-grid opacity-40" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <div className="container-page relative py-14">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-6">
          <div className="lg:col-span-2">
            <Logo variant="mono-light" />
            <p className="mt-3 max-w-xs text-sm text-ink-300">
              {isSv
                ? "CasinoMenu.se är menyn för svenska casinospelare. Vi jämför licenser, betalningar, bonusar och spel – på en plats."
                : "CasinoMenu.se is the menu for Swedish casino players. We compare licenses, payments, bonuses and games — in one place."}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="pill-dark">18+</span>
              <a className="pill-dark" href="https://www.spelpaus.se/">Spelpaus.se</a>
              <a className="pill-dark" href="tel:020819100">Stödlinjen 020-81 91 00</a>
            </div>
          </div>

          {cols.map((col) => (
            <div key={col.title}>
              <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-white/70">
                {col.title}
              </h4>
              <ul className="mt-3 space-y-2 text-sm">
                {col.links.map((l) => (
                  <li key={l.href}>
                    <Link
                      to={l.href}
                      className="text-ink-200 no-underline hover:text-white"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-10 grid gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-ink-200">
          <p>
            <strong className="text-white">
              {isSv ? "Ansvarsfullt spelande:" : "Responsible gambling:"}
            </strong>{" "}
            {t("responsible.notice")}
          </p>
          <p>
            <strong className="text-white">
              {isSv ? "Affiliateinformation:" : "Affiliate disclosure:"}
            </strong>{" "}
            {t("affiliate.disclosure")}
          </p>
        </div>

        <div className="mt-8 flex flex-col items-start justify-between gap-3 border-t border-white/10 pt-6 text-xs text-ink-300 md:flex-row md:items-center">
          <span>{t("footer.copyright")}</span>
          <div className="flex flex-wrap gap-4">
            <Link to={`${prefix}/${isSv ? "om-oss" : "about"}`} className="text-ink-300 no-underline hover:text-white">
              {t("footer.about")}
            </Link>
            <Link to={`${prefix}/${isSv ? "kontakt" : "contact"}`} className="text-ink-300 no-underline hover:text-white">
              {t("footer.contact")}
            </Link>
            <Link to={`${prefix}/${isSv ? "anvandarvillkor" : "terms"}`} className="text-ink-300 no-underline hover:text-white">
              {t("footer.terms")}
            </Link>
            <Link to={`${prefix}/${isSv ? "integritetspolicy" : "privacy"}`} className="text-ink-300 no-underline hover:text-white">
              {t("footer.privacy")}
            </Link>
            <Link to={`${prefix}/affiliate`} className="text-ink-300 no-underline hover:text-white">
              {t("footer.affiliate")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
